import React, { useEffect, useState } from 'react';

import MaUTable from '@material-ui/core/Table';
import PropTypes from 'prop-types';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Skeleton from '@material-ui/lab/Skeleton';
import Button from '../../common/Button';
import ButtonMui from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import {
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Search from './searchBox';
import { TablePagination } from '@material-ui/core';
import RangeDatePicker from '../RangeDatePicker';

// Create an editable cell renderer
const LIMIT_PER_PAGE = 25;

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  setPage,
  onSearch,
  style,
}) {
  const count = preGlobalFilteredRows.length;

  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 600);

  return (
    <span>
      <Search
        style={style}
        value={value}
        onChange={(e) => {
          if (onSearch) {
            onSearch(e);
            setPage(0);
          } else {
            setValue(e.target.value);
            onChange(e.target.value);
            setPage(0);
          }
        }}
      />
    </span>
  );
}

const EnhancedTable = ({
  columns,
  data,
  setData,
  tableName,
  loader,
  updateMyData,
  skipPageReset,
  onClickButton,
  buttonLabel,
  headerActions,
  isLoadingData,
  //for footer table
  currentPage,
  onChangePage,
  totalPages,
  onSearch,
  style,
  //isDynamic if brings pages from back
  isDynamic = false,
  defaultRowsPerPage = LIMIT_PER_PAGE,
  filter = null
}) => {
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { globalFilter },
  } = useTable(
    {
      columns,
      data,
      autoResetPage: !skipPageReset,
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData: (rowIndex, columnId, value) => {
        const newData = [...data];
        newData[rowIndex][columnId] = value;
        setData(newData);
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    isDynamic ? data.length : defaultRowsPerPage || LIMIT_PER_PAGE
  );
  const fallbackRowsPerPage = defaultRowsPerPage || LIMIT_PER_PAGE;
  const effectiveRowsPerPage =
    rowsPerPage && rowsPerPage > 0 ? rowsPerPage : fallbackRowsPerPage;

  useEffect(() => {
    if (isDynamic) {
      setRowsPerPage(data.length);
    } else {
      setRowsPerPage(defaultRowsPerPage || LIMIT_PER_PAGE);
    }
  }, [isDynamic, data.length, defaultRowsPerPage]);

  useEffect(() => {
    if (!isDynamic) {
      const maxPageIndex = Math.max(
        0,
        Math.ceil((rows.length || 0) / (effectiveRowsPerPage || LIMIT_PER_PAGE)) - 1
      );
      if (page > maxPageIndex) {
        setPage(maxPageIndex);
      }
    }
  }, [isDynamic, rows.length, effectiveRowsPerPage, page]);

  const safeTotalPages = Math.max(0, totalPages || 0);
  const safeCurrentPage = Math.max(1, currentPage || 1);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const StyledTableRow = withStyles((theme) => ({
    root: {
      '&.error': {
        backgroundColor: '#FFF3F2',
      },
      '&.info': {
        backgroundColor: '#F1FCFF',
      },
      '&.success': {
        backgroundColor: '#EBFDF0',
      },
      '&.warning': {
        backgroundColor: '#FFFBEE',
      },
      '&.default': {
        backgroundColor: '#FFFFFF',
      },
    },
  }))(TableRow);

  // Enhanced page change handler with validation for dynamic mode
  const handleEnhancedChangePage = (event, newPage) => {
    if (isDynamic) {
      const maxPageIndex = Math.max(0, safeTotalPages - 1);
      const boundedPageIndex = Math.min(Math.max(0, newPage), maxPageIndex);

      if (boundedPageIndex !== safeCurrentPage - 1 && onChangePage) {
        onChangePage(event, boundedPageIndex + 1);
      }
    } else {
      handleChangePage(event, newPage);
    }
  };

  return (
    <>
      <TableContainer className="table-container">
        <div className="actions-table">
          <h1 className="actions-table__title">{tableName}</h1>
          <div className="actions-table__right">
            {filter && <div className="actions-table__filters">{filter}</div>}
            <div className="actions-table__action-container">
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                setPage={setPage}
                onSearch={onSearch}
                style={style}
              />

              {loader && <CircularProgress color="secondary" />}
              {headerActions}
              {onClickButton && (
                <Button
                  className="actions-table__button button"
                  type={'button'}
                  onClick={onClickButton}
                >
                  {buttonLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
        <MaUTable {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
              >
                {headerGroup.headers.map((column) => {
                  return (
                    <TableCell
                      {...(column.id === 'selection'
                        ? column.getHeaderProps()
                        : column.getHeaderProps(column.getSortByToggleProps()))}
                    >
                      {column.render('Header')}
                      {column.id !== 'selection' ? (
                        <TableSortLabel
                          active={column.isSorted}
                          // react-table has a unsorted state which is not treated here
                          direction={column.isSortedDesc ? 'desc' : 'asc'}
                        />
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoadingData
              ? [1, 2, 3, 4, 5].map((number, index) => (
                  <TableRow
                    key={(
                      number + Math.floor(Math.random() * 120 * 1022222)
                    ).toString()}
                  >
                    {headerGroups.map((headerGroup) =>
                      headerGroup.headers.map(() => (
                        <TableCell
                          key={Math.floor(
                            Math.random() * 120 * Math.random() * 120
                          ).toString()}
                        >
                          <Skeleton />
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                ))
              : isDynamic
              ? rows.map((row, i) => {
                  prepareRow(row);
                  {
                  }

                  return (
                    <StyledTableRow
                      className={`${row.original?.bgColor} ${
                        row.original?.estado ? '' : 'inactive'
                      }`}
                      hover
                      tabIndex={-1}
                      {...row.getRowProps()}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })
              : rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    prepareRow(row);
                    return (
                      <StyledTableRow
                        className={row.original?.bgColor}
                        {...row.getRowProps()}
                      >
                        {row.cells.map((cell) => {
                          return (
                            <TableCell {...cell.getCellProps()}>
                              {cell.render('Cell')}
                            </TableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
            <TableRow>
              <TablePagination
                labelRowsPerPage="" //"Filas por pagina:"
                rowsPerPageOptions={[]} //{[5, 10, 25]}
                count={isDynamic ? safeTotalPages * effectiveRowsPerPage : rows.length}
                labelDisplayedRows={(info) => {
                  if (isDynamic) {
                    const labelTotal = safeTotalPages;
                    const labelCurrent = labelTotal > 0 ? safeCurrentPage : 0;
                    const labelWord = labelTotal === 1 ? 'pagina' : 'paginas';
                    return `${labelCurrent} ${labelWord} de ${labelTotal} ${labelWord}`;
                  }
                  return `${info.from}-${info.to} de ${
                    info.count !== -1 ? info.count : `more than ${info.to}`
                  }`;
                }}
                rowsPerPage={effectiveRowsPerPage}
                page={isDynamic ? Math.max(0, safeCurrentPage - 1) : page}
                onPageChange={handleEnhancedChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableBody>
        </MaUTable>
      </TableContainer>
    </>
  );
};

EnhancedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  loader: PropTypes.bool,
  headerActions: PropTypes.node,
  defaultRowsPerPage: PropTypes.number,
};

export default EnhancedTable;










